import { useEffect, useState } from "react";
import { RoundsTimeline } from "./RoundsTimeline";
import { EarlyBird, EARLY_BIRD_ON } from "./EarlyBird";
import { prefetchEnterRound } from "./RoundsGrid";
import { RoundFeatureCard } from "./RoundFeatureCard";
import { mainRound } from "../hooks/useRounds";
import "./rounds.css";

/*
  Landing page bottom section (client pick: mockup M3 shape, restyled).
  Yellow band. Heading "Sign up for Ultimate Lap." Six-node timeline in black
  (next round = live pulsing dot). One wide FLAT black card, white type:
    line 1  Round N
    line 2  <venue> - <layout> Circuit
    line 3  date (large, tech numerals)
    full-width CTA under the text column; rounded photo bucket on the right.
  Only the NEXT round can be entered; later rounds show a greyed "Opening soon"
  (client, 2026-09-08 - may open all rounds later). No status line, no countdown.
*/
export default function Rounds({ rounds = [] }) {
  // Lifecycle (client, 2026-10-19): the featured round is the API's `is_main`
  // (the live round on event day, else the next one).
  const next = mainRound(rounds) || rounds.find((r) => r.status === "upcoming");
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    if (selected == null && rounds.length) setSelected((next || rounds[rounds.length - 1]).n);
  }, [rounds, next, selected]);

  const r = rounds.find((x) => x.n === selected) || next || rounds[0];

  // Warm the microsite + sign-up chunks once the landing page is idle.
  useEffect(() => {
    prefetchEnterRound();
    const run = () => import("../pages/UltimateLap").catch(() => {});
    if ("requestIdleCallback" in window) window.requestIdleCallback(run, { timeout: 2500 });
    else setTimeout(run, 800);
  }, []);

  // Warm every round cover once idle, so tapping a round on the timeline swaps
  // the photo instantly instead of waiting on a fresh download (client, 2026-09-17).
  useEffect(() => {
    if (!rounds.length) return undefined;
    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      rounds.forEach((x) => { if (x.image) { const im = new Image(); im.decoding = "async"; im.src = x.image; } });
    };
    const id = "requestIdleCallback" in window ? window.requestIdleCallback(warm, { timeout: 4000 }) : setTimeout(warm, 1500);
    return () => { cancelled = true; if ("cancelIdleCallback" in window) window.cancelIdleCallback(id); else clearTimeout(id); };
  }, [rounds]);


  return (
    <section className="gm-rounds theme-yellow" id="rounds" data-testid="rounds-section">
      <div className="container-gm">
        <div className="gm-rounds__head">
          <h2 className="display-md gm-rounds__title" data-reveal="mask" data-testid="rounds-heading">
            <span className="mask-line"><span className="gm-rounds__caps">Enter Ultimate Lap Championship</span></span>
          </h2>
        </div>

        {EARLY_BIRD_ON && <EarlyBird tone="light" compact fee={rounds[0]?.fee_aed} vatPct={rounds[0]?.vat_pct} testid="landing-early-bird" />}

        {rounds.length > 0 && (
          <RoundsTimeline rounds={rounds} selected={selected} onSelect={setSelected} />
        )}

        {/* The wide Round card (shared with the Ultimate Lap hero slot, 2026-10-08). */}
        <RoundFeatureCard round={r} tone="light" testid="rounds-card" />
      </div>
    </section>
  );
}
